/**
 * ProductThumb — small rounded product image with a neutral placeholder.
 *
 * @description
 *   A compact rounded MUI Avatar for a product's image, with a neutral
 *   Inventory2 outline icon fallback on an `action.hover` background when no
 *   image is available (or the URL 404s). Extracted from the idiom inlined in
 *   ViewHeader's header avatar so list rows and headers render product images
 *   consistently. Purely presentational — renders no interactive element, so it
 *   is safe inside an already-clickable row / card / button.
 *
 * @useWhen
 *   - A product list row / table cell needs a small leading image
 *   - Any compact product representation with a graceful no-image state
 *   - Inside an already-clickable container (decorative, no nested tab stop)
 *
 * @dontUseFor
 *   - The page / entity header thumbnail — use ViewHeader's `image` prop instead
 *   - A large hero / gallery image (this is a fixed small avatar)
 *   - A clickable image that must navigate on its own (keep it decorative)
 *
 * @related
 *   - ViewHeader — shares the rounded-Avatar + Inventory2 fallback idiom in its header
 *
 * @reuseHints
 *   - RMA replacement-product picker rows
 *   - Recommendation / spare-parts relation lists
 *   - PO line-item product references
 *
 * @props
 *   - src: image URL | null — when falsy, renders the neutral placeholder icon
 *   - alt: string (default '') — keep '' when a visible product name sits beside it
 *           (decorative; screen readers skip it). Pass a name only when informative.
 *   - size: number (default 36) — square px size of the avatar
 *   - sx: object — extra MUI sx merged onto the Avatar
 */
import React from 'react';
import { Avatar } from '@mui/material';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';

export default function ProductThumb({ src, alt = '', size = 36, sx }) {
  return (
    <Avatar
      variant="rounded"
      src={src || undefined}
      alt={alt}
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        bgcolor: 'action.hover',
        ...sx,
      }}
    >
      <Inventory2OutlinedIcon fontSize="small" color="disabled" />
    </Avatar>
  );
}
